import { useCallback, useEffect, useRef, useState } from 'react';
import { createId } from '../lib/ids';
import type { ImageAttachment } from '../types/chat';

export const MAX_IMAGE_ATTACHMENTS = 5;

/**
 * Composer image queue — in memory only, like chat messages. Owns the preview
 * object URLs, so every path that drops an image also revokes its URL.
 */
export function useImageAttachments() {
  const [attachments, setAttachments] = useState<ImageAttachment[]>([]);
  const attachmentsRef = useRef<ImageAttachment[]>([]);

  const commit = useCallback((next: ImageAttachment[]) => {
    attachmentsRef.current = next;
    setAttachments(next);
  }, []);

  const addAttachments = useCallback(
    (files: File[]) => {
      const current = attachmentsRef.current;
      const room = MAX_IMAGE_ATTACHMENTS - current.length;
      if (room <= 0) return;
      const added = files.slice(0, room).map((file) => ({
        id: createId('img'),
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      commit([...current, ...added]);
    },
    [commit],
  );

  const removeAttachment = useCallback(
    (id: string) => {
      const current = attachmentsRef.current;
      const target = current.find((a) => a.id === id);
      if (!target) return;
      URL.revokeObjectURL(target.previewUrl);
      commit(current.filter((a) => a.id !== id));
    },
    [commit],
  );

  const clearAttachments = useCallback(() => {
    if (attachmentsRef.current.length === 0) return;
    attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    commit([]);
  }, [commit]);

  useEffect(
    () => () => {
      attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    },
    [],
  );

  return { attachments, addAttachments, removeAttachment, clearAttachments };
}
